import { useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import { accessSelector, accountSelector, jwtTokenSelector } from '@store/app/app.selector';
import { canEditEopa, isEditor, isFull } from '@auth/permissions';
import { accessTokenIdFromToken } from '@auth/token';
import type { UserType } from '@/types';

/** The caller's REP grant in the shape the pages gate on. */
export const useAccess = () => {
  const access = useAppSelector(accessSelector);
  const account = useAppSelector(accountSelector);
  const jwtToken = useAppSelector(jwtTokenSelector);
  return useMemo(
    () => ({
      access,
      account,
      userType: (access?.isStateUser ? 'STATE_USER' : 'COUNTY_USER') as UserType,
      isStateUser: !!access?.isStateUser,
      isCountyUser: !!access && !access.isStateUser,
      /** Group id the county user acts for (also the lcert/equipment pvCounty). */
      groupId: access?.groupId ?? 0,
      groupName: access?.groupName ?? '',
      permission: access?.type,
      /** F or L */
      canEdit: isEditor(access),
      /** F */
      isFull: isFull(access),
      /** F, L or C */
      canEditEopa: canEditEopa(access),
      userId: account?.id ?? 0,
      /**
       * The loopback access-token id the documents service still authenticates
       * with (legacy `userAuthCreds.id`): the `accessToken` the portal returns
       * with the verified account, else the one inside the id_token's chunk claim.
       */
      accessTokenId: account?.accessToken || accessTokenIdFromToken(jwtToken) || '',
      userName: `${account?.pvPersonGivenName || ''} ${account?.pvPersonSurName || ''}`.trim(),
    }),
    [access, account, jwtToken],
  );
};
